import { useTreatmentWithConfig } from '@splitsoftware/splitio-react';
import { useMemo } from 'react';
import {
  DEFAULT_FEATURED_DESTINATIONS,
  DEFAULT_POPULAR_DESTINATIONS,
} from '../constants/defaultDestinations';
import type {
  DestinationCardData,
  DestinationsConfig,
  RawDestinationCardData,
} from '../types/destinationCards';

const FEATURED_POPULAR_DESTINATIONS_FLAG =
  'enable-popular-featured-destinations';

export interface UseFeaturedPopularDestinationsResult {
  featured: DestinationCardData[];
  popular: DestinationCardData[];
  isEnabled: boolean;
  isReady: boolean;
  hasTimedout: boolean;
}

const formatImageUrl = (image: string): string => {
  if (!image) return '';
  if (image.startsWith('http://') || image.startsWith('https://')) {
    return image;
  }
  return `https://assets.ace.aaa.com/is/image/aceaaa/${image}:cards-strd-600x400`;
};

const mapToDestinationCardData = (
  item: RawDestinationCardData,
): DestinationCardData => ({
  name: item.name,
  searchTerm: item.searchTerm || item.name,
  image: formatImageUrl(item.image),
});

const useFeaturedPopularDestinations =
  (): UseFeaturedPopularDestinationsResult => {
    const {
      treatment: treatmentResult,
      isReady,
      hasTimedout,
    } = useTreatmentWithConfig({
      name: FEATURED_POPULAR_DESTINATIONS_FLAG,
    });

    const isEnabled = treatmentResult?.treatment === 'on';
    const config = treatmentResult?.config;

    const destinations = useMemo(() => {
      if (!config) {
        return {
          featured: DEFAULT_FEATURED_DESTINATIONS,
          popular: DEFAULT_POPULAR_DESTINATIONS,
        };
      }

      try {
        const parsed: DestinationsConfig = JSON.parse(config);
        const featured =
          Array.isArray(parsed?.featured) && parsed.featured.length > 0
            ? parsed.featured.map(mapToDestinationCardData)
            : DEFAULT_FEATURED_DESTINATIONS;
        const popular =
          Array.isArray(parsed?.popular) && parsed.popular.length > 0
            ? parsed.popular.map(mapToDestinationCardData)
            : DEFAULT_POPULAR_DESTINATIONS;

        return { featured, popular };
      } catch {
        return {
          featured: DEFAULT_FEATURED_DESTINATIONS,
          popular: DEFAULT_POPULAR_DESTINATIONS,
        };
      }
    }, [config]);

    return {
      featured: destinations.featured,
      popular: destinations.popular,
      isEnabled,
      isReady,
      hasTimedout,
    };
  };

export default useFeaturedPopularDestinations;
